import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import CardDeckView from '../../../component/cardDeck/CardDeckView';
import ProductsListItem from '../../../component/productsList/productsListItem/ProductsListItem';
import SortDropdown from '../../search/searchSort/SortDropdown';
import { getSortOptionsArray, SORT_OPTIONS } from '../../search/searchSort/SortOptions';

import '../../../component/productsList/ProductsList.css';
import './ProductModelResults.css';

const MODEL_SORT_CODES = [
    SORT_OPTIONS.POPULAR.code,
    SORT_OPTIONS.NEW.code,
    SORT_OPTIONS.LIKE.code,
    SORT_OPTIONS.DISCOUNT.code,
    SORT_OPTIONS.LOW_PRICE.code,
    SORT_OPTIONS.HIGH_PRICE.code,
];

const EAGER_IMAGE_COUNT = 4;

/**
 * 브랜드 모델 페이지의 정렬 툴바 + 상품 그리드(무한 스크롤).
 * 데이터/페이지네이션은 부모(ProductModel)의 단일 nested 쿼리를 공유하고,
 * 리스팅 렌더링은 공통 컴포넌트 CardDeckView 에 위임한다(Brand/Search 와 동일한 그리드).
 * 정렬 UI 도 Brand/Search 와 동일한 공용 SortDropdown 을 재사용한다.
 * 여기서는 정렬 UI 와 더보기(fetchMore, nested 병합)만 담당한다.
 */
export default function ProductModelResults({
    items,
    total,
    loading,
    fetchMore,
    sort,
    onSortChange,
    schemaOrgPreview,
}) {
    const hasMore = items.length < total;

    const loadMore = () => {
        fetchMore({
            variables: { offset: items.length },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const prevContainer = previousResult?.getCatalogModelWithProducts;
                const moreContainer = fetchMoreResult?.getCatalogModelWithProducts;
                if (!prevContainer || !moreContainer) {
                    return previousResult;
                }

                return {
                    ...previousResult,
                    getCatalogModelWithProducts: {
                        ...prevContainer,
                        products: {
                            ...moreContainer.products,
                            items: [
                                ...prevContainer.products.items,
                                ...moreContainer.products.items,
                            ],
                        },
                    },
                };
            },
        });
    };

    const sortOptions = useMemo(
        () => getSortOptionsArray().filter((opt) => MODEL_SORT_CODES.includes(opt.code)),
        []
    );

    return (
        <div className="ProductModelResults">
            <div className="ProductModelResults-toolbar">
                <div className="ProductModelResults-count">
                    <strong>{(total || 0).toLocaleString()}</strong> listings
                </div>
                <div className="ProductModelResults-sort">
                    <SortDropdown
                        selectedSort={sort}
                        onSortChange={onSortChange}
                        sortOptions={sortOptions}
                    />
                </div>
            </div>

            <div className="ProductsList">
                <CardDeckView
                    items={items}
                    loading={loading}
                    hasMore={hasMore}
                    loadMore={loadMore}
                    offset={0}
                    rowClassName="row-cols-2 row-cols-md-3 row-cols-xl-4"
                    isEmptyMsg
                    schemaOrgPreview={schemaOrgPreview}
                >
                    {(product, index) => (
                        <div className="ProductsList-item">
                            <ProductsListItem
                                product={product}
                                imageLoading={index < EAGER_IMAGE_COUNT ? 'eager' : 'lazy'}
                                imageFetchPriority={index === 0 ? 'high' : undefined}
                            />
                        </div>
                    )}
                </CardDeckView>
            </div>
        </div>
    );
}

ProductModelResults.propTypes = {
    items: PropTypes.array.isRequired,
    total: PropTypes.number,
    loading: PropTypes.bool,
    fetchMore: PropTypes.func.isRequired,
    sort: PropTypes.string.isRequired,
    onSortChange: PropTypes.func.isRequired,
    schemaOrgPreview: PropTypes.object,
};
